
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { formatOrderItem } from '@/utils/format';
import { isReplenishSection, isExtensionSection, isReplenishOrExtensionSection, isEventSection } from '@/utils/general';
import Toast from '@/utils/toast';
import OrderModifiers from './OrderModifiers.vue';
import QuantityInput from '@/components/shared/suites/QuantityInput.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import ImageDisplayModal from '@/components/shared/ImageDisplayModal.vue';
import TagPreview from '@/components/shared/TagPreview.vue';

const namespace = 'orders';

@Component<OrderItemViewer>({
	components: {
		OrderModifiers,
		QuantityInput,
		StandardModal,
		TextButton,
		ImageDisplayModal,
		TagPreview
	}
})
export default class OrderItemViewer extends Vue {
	@Action('setSelectedItem', { namespace }) updateSelectedItem!: (item: MenuItem | null) => void;
	@Action('setOrderItemOptions', { namespace }) setOrderItemOptions!: (optionGroups: OrderOptionGroup[]) => void;
	@Action('setOrderItemQuantity', { namespace }) setOrderItemQuantity!: (quantity: number) => void;
	@Action('setEditingItem', { namespace }) setEditingItem!: (editing: boolean) => void;
	@Action('addToCart', { namespace }) addToCart!: (specialInstructions: string) => void;
	@Action('removeFromOrder', { namespace }) removeFromOrder!: (index: number) => void;
	@Action('resetItemViewerState', { namespace }) resetItemViewerState!: () => void;
	@Action('setOrderItem', { namespace }) setOrderItem!: (orderItem: MenuOrderItem) => void;
	@Getter('getSelectedItem', { namespace }) item!: MenuItem;
	@Getter('getOrderItem', { namespace }) orderItem!: MenuOrderItem;
	@Getter('getOrderItemOptions', { namespace }) orderItemOptions!: OrderOptionGroup[];
	@Getter('getCartItems', { namespace }) cartItems!: MenuOrderItem[];
	@Getter('getCart', { namespace }) cart!: Cart;
	@Getter('getEditingItem', { namespace }) isEditing!: boolean;
	@Getter('getModifyingOrder', { namespace }) isModifyingOrder!: boolean;
	@Getter('getOrderItemPrice', { namespace }) orderItemPrice!: number;
	@Getter('getOrderDetails', { namespace }) orderDetails!: OrderDetails;
	@Getter('getSelectedSectionSlug', { namespace }) selectedSectionSlug!: string;
	@Getter('getMaxItemsPerCart', { namespace: 'auth' }) maxItemsPerCart!: number;
	@Getter('hideItemsSpecialInstructions', { namespace: 'auth' }) private hideItemsSpecialInstructions!: boolean;
	@Getter('areOrdersPaused', { namespace: 'auth' }) private areOrdersPaused!: boolean;
	@Prop({ type: Boolean, default: false }) private kioskMode!: boolean;

	$refs!: { modifiers: OrderModifiers; };

	private formattedItem = {} as MenuOrderItem;
	private specialInstructions: string = '';
	private addingToOrder: boolean = false;
	private showNutritionalModal: boolean = false;

	// Make utility functions available in template
	private isReplenishSection = isReplenishSection;
	private isExtensionSection = isExtensionSection;
	private isReplenishOrExtensionSection = isReplenishOrExtensionSection;
	private isEventSection = isEventSection;

	private get mappedTags(): MenuItemTag[] {
		return [...(this.item.tags || [])].sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));
	}

	/**
	 * Returns the nutritional sheet as an array for the ImageDisplayModal
	 *
	 * @return {object[]}
	 */
	private get nutritionalImages(): { url: string; alt: string }[] {
		return this.item.nutritional_sheet_url ? [{
			url: this.item.nutritional_sheet_url,
			alt: this.$t('orders.item_viewer.nutrition_sheet_alt')
		}] : [];
	}

	/**
	 * Don't display modifiers if there are no option groups or option groups with no options
	 * This can happen if pricing options were enabled and then disabled, a pricing option group will still exist
	 *
	 * @return {boolean}
	 */
	private get showModifiers(): boolean {
		return !!(this.formattedItem.orderOptions?.length && this.formattedItem.orderOptions.some(option => option.values.length));
	}

	/**
	 * Returns the number of remaining characters for the special instructions
	 *
	 * @return {number}
	 */
	private get remainingCharacters(): number {
		return 300 - this.specialInstructions.length;
	}


	/**
	 * Gets the appropriate text for the add to order button
	 *
	 * @return {string}
	 */
	private get getAddToOrderButtonText(): string {
		if (this.areOrdersPaused && this.kioskMode) {
			return this.$t('orders.item_viewer.unavailable');
		}

		return this.$t('orders.item_viewer.add_to_order_btn', { price: this.$n(this.orderItemPrice, 'currency') });
	}

	private created(): void {
		this.formattedItem = formatOrderItem(this.item);
		const memo = this.orderItemOptions?.find(option => option.memo);
		if (memo) {
			this.specialInstructions = memo.values[0].name;
		}
	}

	/**
	 * Checks if adding/updating to a specific quantity would exceed cart limits
	 * Only applies to kiosk mode users
	 *
	 * @param {number} targetQuantity - The quantity we want to set for this item
	 * @return {boolean}
	 */
	private wouldExceedCartLimit(targetQuantity: number): boolean {
		// No limits for non-kiosk users or when no limit is configured
		if (!this.maxItemsPerCart || !this.kioskMode) {
			return false;
		}

		// Calculate current total quantity in cart (excluding item being edited)
		let currentTotalQuantity = this.cartItems.reduce((total, item) => total + item.quantity, 0);

		// If we're editing an existing item, subtract its original quantity
		if (this.isEditing) {
			const editingItem = this.cartItems.find(item => item.cartIndex === this.orderItem.cartIndex);
			if (editingItem) {
				currentTotalQuantity -= editingItem.quantity;
			}
		}

		// Check if the new total would exceed the limit
		const newTotalQuantity = currentTotalQuantity + targetQuantity;
		return newTotalQuantity > this.maxItemsPerCart;
	}

	/**
	 * Validates the required options before adding the item to the cart
	 *
	 * @return {void}
	 */
	private validateRequiredOptions(): void {
		if (this.item.options && this.item.options.length && this.$refs.modifiers) {
			this.$refs.modifiers.validateRequiredOptions();
		}
		else {
			this.handleAddToCart();
		}
	}

	/**
	 * Increases the quantity of the order item
	 *
	 * @return {void}
	 */
	private increaseQuantity(): void {
		const newQuantity = this.orderItem.quantity + 1;

		if (this.wouldExceedCartLimit(newQuantity)) {
			Toast.error(this.$t('orders.item_viewer.max_order_items', { limit: this.maxItemsPerCart }));
			return;
		}

		this.setOrderItemQuantity(newQuantity);
	}

	/**
	 * Decreases the quantity of the order item
	 *
	 * @return {void}
	 */
	private decreaseQuantity(): void {
		if (this.orderItem.quantity === 1) {
			return;
		}
		const quantity = this.orderItem.quantity - 1;
		this.setOrderItemQuantity(quantity);
	}

	/**
	 * Handles a quantity typed directly into the quantity input.
	 * Applies the same cart limit guard as increaseQuantity.
	 *
	 * @param {number} quantity
	 * @return {void}
	 */
	private handleDirectQuantityInput(quantity: number): void {
		if (this.wouldExceedCartLimit(quantity)) {
			Toast.error(this.$t('orders.item_viewer.max_order_items', { limit: this.maxItemsPerCart }));
			return;
		}

		this.setOrderItemQuantity(quantity);
	}

	/**
	 * Adds the item to the cart or updates existing item
	 *
	 * @return {Promise<void>}
	 */
	private async handleAddToCart(): Promise<void> {
		// Check if adding/updating this item would exceed cart limits
		if (this.wouldExceedCartLimit(this.orderItem.quantity)) {
			Toast.error(this.$t('orders.item_viewer.max_order_items', { limit: this.maxItemsPerCart }));
			return;
		}

		try {
			this.addingToOrder = true;
			await this.addToCart(this.specialInstructions);
			this.addingToOrder = false;
			this.resetItemViewerState();
		}
		catch (errorMessage) {
			this.addingToOrder = false;
			Toast.error(this.$t('orders.item_viewer.error_adding_to_order', { errorMessage }));
		}
	}

	/**
	 * Prevent closing item if item is being added to the cart
	 *
	 * @return {void}
	 */
	private handleModalClose(): void {
		if (!this.addingToOrder) {
			this.resetItemViewerState();
		}
	}
}
